// Rows for the 전체 모의전 표 보기: one row per registered 모의전 slot, with the member's own best on
// that boss as the 고점 reference.

export const PRACTICE_TABLE_ELEMENTS = ['작열', '풍압', '수냉', '전격', '철갑'];
export const PRACTICE_TABLE_SORT_KEYS = ['nickname', 'boss', 'combo', 'damage', 'peak'];
export const DEFAULT_PRACTICE_TABLE_SORT = Object.freeze({ key: 'damage', direction: 'desc' });

const text = (value) => String(value ?? '').trim();
const normalized = (value) => text(value).replace(/\s+/g, ' ').toLocaleLowerCase('ko-KR');

function memberOpenid(member = {}) {
  return text(member.openid || member.memberOpenid || member.member_openid);
}

function findMember(record, members) {
  const openid = text(record.memberOpenid || record.member_openid);
  if (openid) {
    const byOpenid = members.find((member) => memberOpenid(member) === openid);
    if (byOpenid) return byOpenid;
  }
  const nickname = normalized(record.memberNickname || record.member_nickname || record.nickname);
  return nickname ? members.find((member) => normalized(member?.nickname) === nickname) || null : null;
}

export function buildPracticeTableRows({ bosses = [], members = [], practiceRecords = [] } = {}) {
  const bossByNo = new Map((Array.isArray(bosses) ? bosses : []).map((boss) => [Number(boss.bossNo), boss]));
  const memberList = Array.isArray(members) ? members : [];
  const rows = (Array.isArray(practiceRecords) ? practiceRecords : []).map((record) => {
    const member = findMember(record, memberList);
    const boss = bossByNo.get(Number(record.bossNo)) || null;
    const deck = (Array.isArray(record.deck) ? record.deck : []).map(text).filter(Boolean).slice(0, 5);
    const nickname = text(member?.nickname) || text(record.memberNickname || record.member_nickname) || '이름 없음';
    const memberKey = memberOpenid(member || {}) || text(record.memberOpenid || record.member_openid) || normalized(nickname);
    return {
      id: text(record.id) || `${memberKey}:${record.bossNo}:${record.slotNo}`,
      record,
      member,
      boss,
      memberKey,
      nickname,
      bossNo: Number(record.bossNo) || 0,
      bossName: text(boss?.bossName) || `${Number(record.bossNo) || '-'}보스`,
      element: text(boss?.element),
      slotNo: Number(record.slotNo) || 0,
      deck,
      // The same five NIKKEs in any slot order are one 조합.
      comboKey: [...deck].map((code) => code.toLowerCase()).sort().join('|'),
      damage: Number(record.damageEok) > 0 ? Number(record.damageEok) : 0,
      title: text(record.title),
      peakPercent: null,
    };
  });

  const best = new Map();
  for (const row of rows) {
    const key = `${row.memberKey}:${row.bossNo}`;
    best.set(key, Math.max(best.get(key) || 0, row.damage));
  }
  for (const row of rows) {
    const top = best.get(`${row.memberKey}:${row.bossNo}`) || 0;
    row.peakPercent = row.damage > 0 && top > 0 ? (row.damage / top) * 100 : null;
  }
  return rows;
}

export function filterPracticeTableRows(rows = [], { element = '', query = '' } = {}) {
  const needle = normalized(query);
  return rows.filter((row) => (!element || row.element === element) && (!needle || normalized(row.nickname).includes(needle)));
}

// 가나다 first, then Latin; Hanja by its reading; "멤버2" before "멤버10".
const compareNickname = new Intl.Collator('ko-KR', { numeric: true }).compare;
const byDamageDesc = (a, b) => b.damage - a.damage || compareNickname(a.nickname, b.nickname) || a.slotNo - b.slotNo;

// Every key starts in its natural reading order ('desc'): 닉네임 가나다순, highest 딜량 or 고점% first,
// boss 1 first, the strongest 조합 first. Choosing the same key again ('asc') reverses it.
export function sortPracticeTableRows(rows = [], sort = DEFAULT_PRACTICE_TABLE_SORT) {
  const key = PRACTICE_TABLE_SORT_KEYS.includes(sort?.key) ? sort.key : DEFAULT_PRACTICE_TABLE_SORT.key;
  const flip = sort?.direction === 'asc' ? -1 : 1;
  const list = [...rows];
  // A member's slots stay together in boss then slot order, like the 멤버별 cards; reversing moves whole
  // members, not the slots inside one.
  if (key === 'nickname') {
    return list.sort((a, b) => flip * (compareNickname(a.nickname, b.nickname) || a.memberKey.localeCompare(b.memberKey))
      || a.bossNo - b.bossNo
      || a.slotNo - b.slotNo);
  }
  if (key === 'damage') return list.sort((a, b) => flip * (b.damage - a.damage) || byDamageDesc(a, b));
  if (key === 'peak') {
    return list.sort((a, b) => {
      if (a.peakPercent === null || b.peakPercent === null) return (a.peakPercent === null) - (b.peakPercent === null) || byDamageDesc(a, b);
      return flip * (b.peakPercent - a.peakPercent) || byDamageDesc(a, b);
    });
  }
  // Inside a boss or a 조합 the strongest row comes first.
  if (key === 'boss') return list.sort((a, b) => flip * (a.bossNo - b.bossNo) || byDamageDesc(a, b));
  const comboBest = new Map();
  for (const row of list) comboBest.set(row.comboKey, Math.max(comboBest.get(row.comboKey) || 0, row.damage));
  return list.sort((a, b) => flip * ((comboBest.get(b.comboKey) || 0) - (comboBest.get(a.comboKey) || 0))
    || a.comboKey.localeCompare(b.comboKey)
    || byDamageDesc(a, b));
}

export function nextPracticeTableSort(current = DEFAULT_PRACTICE_TABLE_SORT, key = 'damage') {
  if (current?.key === key) return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' };
  return { key, direction: 'desc' };
}
